import React from 'react';
import { useTranslation } from 'react-i18next';
import { createSelector } from '@reduxjs/toolkit';
import { generatePath, useHistory } from 'react-router-dom';

import type { ITreeFolder } from '@/components/common/resources/types';
import type { ISelectedFolder } from '@/store/types';

import { useAppSelector, useResourceNavigation } from '@/hooks';
import { classList, compareSelectedFolder, isChildrenSelected, isOfType } from '@/helpers';
import { selectFoldersList } from '@/store/selectors/resourcesSelectors';
import { TreeFolderAccordion } from '@/components/common';
import { FolderItemDatabase } from '.';
import { ResourceTypeId } from '@/store/enums';
import routes from '@/config/routes';

const folderDatabasesSelector = createSelector(selectFoldersList, foldersList => ({ foldersList }));

const FolderDatabases = (props: { data: ITreeFolder }): React.ReactElement => {
  const { t } = useTranslation();
  const resourceDetails = { clientId: props.data.clientId, folderType: 'database' } as const;
  const { selectedFolder } = useResourceNavigation();
  const { foldersList } = useAppSelector(folderDatabasesSelector);
  const history = useHistory();

  const folderSignature: ISelectedFolder = {
    clientId: resourceDetails.clientId,
    folderId: ResourceTypeId.database,
    folderType: 'category',
  };
  const isSelected = !!selectedFolder && compareSelectedFolder(selectedFolder, folderSignature);

  const handleOnSelect = () => {
    if (!isSelected) {
      history.push(
        generatePath(routes.sp.scheduling.resources.category, {
          client: resourceDetails.clientId,
          resourceType: resourceDetails.folderType,
        }),
      );
    }
  };

  if (isOfType<ITreeFolder>(props.data, 'database') === false) {
    return <></>;
  }
  return (
    <TreeFolderAccordion
      title={t`resources.leftPanel.folderDatabases`}
      onSelectCallback={handleOnSelect}
      className={classList(isSelected && 'accordion-title-selected')}
      isChildrenSelected={
        selectedFolder ? isChildrenSelected(folderSignature, selectedFolder, foldersList || []) : false
      }
    >
      {props.data.database.map((data, id) => (
        <FolderItemDatabase key={`${data.id}_${id}`} data={data} resourceDetails={resourceDetails} eventKey={id} />
      ))}
    </TreeFolderAccordion>
  );
};

export { FolderDatabases };
